import React from 'react';

/** Shared node style for architecture canvases */
function FlowNode({ x, y, w, h, title, sub, accent = '#00E5CC' }) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx="10"
        fill="rgba(13,31,28,0.95)"
        stroke={accent}
        strokeOpacity="0.45"
        strokeWidth="1.5"
      />
      <text x={x + w / 2} y={y + h / 2 - (sub ? 6 : 0)} textAnchor="middle" fill="#FFFFFF" fontSize="12" fontFamily="Outfit, sans-serif" fontWeight="700">
        {title}
      </text>
      {sub && (
        <text x={x + w / 2} y={y + h / 2 + 12} textAnchor="middle" fill="#9ECDC6" fontSize="10" fontFamily="DM Sans, sans-serif">
          {sub}
        </text>
      )}
    </g>
  );
}

function Arrow({ x1, y1, x2, y2, color = '#00E5CC' }) {
  const mid = (x1 + x2) / 2;
  return (
    <g>
      <path d={`M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`} fill="none" stroke={color} strokeWidth="1.5" strokeOpacity="0.55" />
      <polygon points={`${x2},${y2} ${x2 - 6},${y2 - 4} ${x2 - 6},${y2 + 4}`} fill={color} fillOpacity="0.8" />
    </g>
  );
}

/**
 * PromptLens pipeline canvas — optimize → secure → route → execute → analytics
 */
export function PromptLensArchitectureFlow({ compact = false }) {
  const h = compact ? 220 : 280;
  const nodes = compact
    ? [
        { x: 20, title: 'Prompt', sub: 'VS Code / Chat' },
        { x: 160, title: 'Optimize', sub: 'Token trim' },
        { x: 300, title: 'Secure', sub: 'PII / Secrets' },
        { x: 440, title: 'Route', sub: 'Fast·Balanced·Expert' },
        { x: 580, title: 'LLM', sub: 'Provider call' },
        { x: 720, title: 'Analytics', sub: 'Cost & savings' },
      ]
    : [
        { x: 24, title: 'User Prompt', sub: 'In-editor gateway' },
        { x: 168, title: 'Optimize', sub: 'Token compaction' },
        { x: 312, title: 'Security', sub: 'Screen & redact' },
        { x: 456, title: 'Classify', sub: 'Complexity' },
        { x: 600, title: 'Route Tier', sub: 'Model selection' },
        { x: 744, title: 'Execute', sub: 'Stream response' },
        { x: 888, title: 'Cost Lens', sub: 'Usage analytics' },
      ];

  const w = compact ? 860 : 1040;
  const nodeW = compact ? 110 : 118;
  const nodeH = compact ? 56 : 64;
  const y = compact ? 80 : 100;

  return (
    <div className="w-full overflow-x-auto rounded-2xl border" style={{ borderColor: 'var(--jb-border)', background: 'var(--jb-forest-card)' }}>
      <div className="px-4 pt-4 pb-1 flex items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[0.65rem] text-cyan uppercase tracking-widest">Architecture</p>
          <h3 className="font-display font-bold text-text-hero text-sm sm:text-base">PromptLens pipeline</h3>
        </div>
        <span className="font-mono text-[0.65rem] text-text-muted hidden sm:inline">RIGHT-SIZE · ROUTE · REDUCE · REPEAT</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[640px] h-auto" role="img" aria-label="PromptLens architecture flow">
        <defs>
          <linearGradient id="plGlow" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00E5CC" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#22C55E" stopOpacity="0.06" />
          </linearGradient>
        </defs>
        <rect width={w} height={h} fill="url(#plGlow)" />
        {nodes.map((n, i) => (
          <React.Fragment key={n.title}>
            <FlowNode x={n.x} y={y} w={nodeW} h={nodeH} title={n.title} sub={n.sub} accent={i === nodes.length - 1 ? '#22C55E' : '#00E5CC'} />
            {i < nodes.length - 1 && (
              <Arrow x1={n.x + nodeW} y1={y + nodeH / 2} x2={nodes[i + 1].x} y2={y + nodeH / 2} />
            )}
          </React.Fragment>
        ))}
        {!compact && (
          <text x={w / 2} y={h - 28} textAnchor="middle" fill="#5E9E96" fontSize="11" fontFamily="DM Sans, sans-serif">
            Security and cost controls applied before tokens leave the editor
          </text>
        )}
      </svg>
    </div>
  );
}

/**
 * OmniLens hub-and-spoke FinOps canvas
 */
export function OmniLensArchitectureFlow({ compact = false }) {
  const w = compact ? 860 : 1040;
  const h = compact ? 260 : 340;

  return (
    <div className="w-full overflow-x-auto rounded-2xl border" style={{ borderColor: 'var(--jb-border)', background: 'var(--jb-forest-card)' }}>
      <div className="px-4 pt-4 pb-1 flex items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[0.65rem] text-cyan uppercase tracking-widest">Architecture</p>
          <h3 className="font-display font-bold text-text-hero text-sm sm:text-base">OmniLens hub-and-spoke</h3>
        </div>
        <span className="font-mono text-[0.65rem] text-text-muted hidden sm:inline">FOCUS 1.0 · MULTI-CLOUD</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[640px] h-auto" role="img" aria-label="OmniLens architecture flow">
        <defs>
          <linearGradient id="olGlow" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#E07A3D" stopOpacity="0.1" />
            <stop offset="50%" stopColor="#00E5CC" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#22C55E" stopOpacity="0.08" />
          </linearGradient>
        </defs>
        <rect width={w} height={h} fill="url(#olGlow)" />

        {/* Spokes */}
        <FlowNode x={40} y={40} w={130} h={56} title="AWS CUR" sub="Cost Explorer" accent="#E07A3D" />
        <FlowNode x={40} y={compact ? 110 : 130} w={130} h={56} title="Azure" sub="Cost Mgmt" accent="#00E5CC" />
        <FlowNode x={40} y={compact ? 180 : 220} w={130} h={56} title="GCP / OCI" sub="Roadmap" accent="#5E9E96" />

        {/* Arrows to FOCUS */}
        <Arrow x1={170} y1={68} x2={230} y2={compact ? 140 : 170} color="#E07A3D" />
        <Arrow x1={170} y1={compact ? 138 : 158} x2={230} y2={compact ? 140 : 170} />
        <Arrow x1={170} y1={compact ? 208 : 248} x2={230} y2={compact ? 140 : 170} color="#5E9E96" />

        <FlowNode x={230} y={compact ? 112 : 140} w={140} h={64} title="FOCUS Map" sub="Normalize 1.0" />

        <Arrow x1={370} y1={compact ? 144 : 172} x2={430} y2={compact ? 144 : 172} />

        <FlowNode x={430} y={compact ? 104 : 128} w={150} h={80} title="FinOps Hub" sub="Lake · Athena · SQLite" accent="#22C55E" />

        <Arrow x1={580} y1={compact ? 144 : 168} x2={640} y2={compact ? 80 : 90} />
        <Arrow x1={580} y1={compact ? 144 : 168} x2={640} y2={compact ? 144 : 168} />
        <Arrow x1={580} y1={compact ? 144 : 168} x2={640} y2={compact ? 208 : 246} />

        <FlowNode x={640} y={compact ? 52 : 58} w={160} h={56} title="Analytics" sub="Trends · Forecast" />
        <FlowNode x={640} y={compact ? 116 : 140} w={160} h={56} title="Optimize" sub="RI · Right-size · Idle" accent="#E07A3D" />
        <FlowNode x={640} y={compact ? 180 : 222} w={160} h={56} title="Govern" sub="Policies · Audit" />

        {!compact && (
          <>
            <Arrow x1={800} y1={168} x2={860} y2={168} color="#22C55E" />
            <FlowNode x={860} y={136} w={140} h={64} title="Dashboard" sub="KPIs · Alerts" accent="#22C55E" />
          </>
        )}
      </svg>
    </div>
  );
}

export default function ProductArchitectureFlow({ slug, compact = false }) {
  if (slug === 'omnilens') return <OmniLensArchitectureFlow compact={compact} />;
  return <PromptLensArchitectureFlow compact={compact} />;
}

/**
 * PA Museum — React + Azure serverless ops platform
 */
export function MuseumArchitectureFlow({ compact = false }) {
  const w = compact ? 860 : 960;
  const h = compact ? 200 : 240;
  const nodes = [
    { x: 30, title: 'Staff UI', sub: 'React · Roles' },
    { x: 190, title: 'API', sub: 'Azure Functions' },
    { x: 350, title: 'Data', sub: 'NoSQL store' },
    { x: 510, title: 'Rules', sub: 'Conflict detect' },
    { x: 670, title: 'Reports', sub: 'Capacity · Trends' },
  ];
  const nodeW = 130;
  const nodeH = 56;
  const y = compact ? 70 : 90;

  return (
    <div className="w-full overflow-x-auto rounded-2xl border" style={{ borderColor: 'var(--jb-border)', background: 'var(--jb-forest-card)' }}>
      <div className="px-4 pt-4 pb-1 flex items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[0.65rem] text-cyan uppercase tracking-widest">Architecture</p>
          <h3 className="font-display font-bold text-text-hero text-sm sm:text-base">PA Museum platform</h3>
        </div>
        <span className="font-mono text-[0.65rem] text-text-muted hidden sm:inline">REACT · AZURE SERVERLESS</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[640px] h-auto" role="img" aria-label="PA Museum architecture flow">
        <rect width={w} height={h} fill="rgba(0,229,204,0.04)" />
        {nodes.map((n, i) => (
          <React.Fragment key={n.title}>
            <FlowNode x={n.x} y={y} w={nodeW} h={nodeH} title={n.title} sub={n.sub} accent={i === nodes.length - 1 ? '#22C55E' : '#00E5CC'} />
            {i < nodes.length - 1 && (
              <Arrow x1={n.x + nodeW} y1={y + nodeH / 2} x2={nodes[i + 1].x} y2={y + nodeH / 2} />
            )}
          </React.Fragment>
        ))}
      </svg>
    </div>
  );
}

/**
 * Emission Project — multi-source ETL to compliance reports
 */
export function EmissionArchitectureFlow({ compact = false }) {
  const w = compact ? 860 : 1000;
  const h = compact ? 240 : 280;

  return (
    <div className="w-full overflow-x-auto rounded-2xl border" style={{ borderColor: 'var(--jb-border)', background: 'var(--jb-forest-card)' }}>
      <div className="px-4 pt-4 pb-1 flex items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[0.65rem] text-cyan uppercase tracking-widest">Architecture</p>
          <h3 className="font-display font-bold text-text-hero text-sm sm:text-base">Emission Project pipeline</h3>
        </div>
        <span className="font-mono text-[0.65rem] text-text-muted hidden sm:inline">ADF · FUNCTIONS · AUDIT</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[640px] h-auto" role="img" aria-label="Emission Project architecture flow">
        <rect width={w} height={h} fill="rgba(34,197,94,0.04)" />
        <FlowNode x={30} y={40} w={120} h={50} title="Sources" sub="CSV · API · DB" accent="#E07A3D" />
        <FlowNode x={30} y={compact ? 120 : 140} w={120} h={50} title="Fleets" sub="Facilities" accent="#E07A3D" />
        <Arrow x1={150} y1={65} x2={210} y2={compact ? 120 : 140} color="#E07A3D" />
        <Arrow x1={150} y1={compact ? 145 : 165} x2={210} y2={compact ? 120 : 140} color="#E07A3D" />
        <FlowNode x={210} y={compact ? 92 : 112} w={140} h={56} title="ADF ETL" sub="Adapters" />
        <Arrow x1={350} y1={compact ? 120 : 140} x2={410} y2={compact ? 120 : 140} />
        <FlowNode x={410} y={compact ? 92 : 112} w={140} h={56} title="Transform" sub="Validate · Lineage" />
        <Arrow x1={550} y1={compact ? 120 : 140} x2={610} y2={compact ? 120 : 140} />
        <FlowNode x={610} y={compact ? 92 : 112} w={140} h={56} title="Reports" sub="Auto · Audit" accent="#22C55E" />
        <Arrow x1={750} y1={compact ? 120 : 140} x2={800} y2={compact ? 120 : 140} color="#22C55E" />
        <FlowNode x={800} y={compact ? 92 : 112} w={140} h={56} title="Dashboard" sub="Power BI" accent="#22C55E" />
      </svg>
    </div>
  );
}

export function ProjectArchitectureFlow({ slug, compact = false }) {
  if (slug === 'emission') return <EmissionArchitectureFlow compact={compact} />;
  if (slug === 'museum') return <MuseumArchitectureFlow compact={compact} />;
  return null;
}

